import { Link, useParams, Navigate, useNavigate } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import {
  GraduationCap, ChatCircleDots, MagnifyingGlass, FileText, CalendarStar, QrCode, ArrowRight, ArrowLeft, CheckCircle, X,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const FEATURES = {
  "education-timeline": {
    icon: GraduationCap,
    title: "Full Education Timeline",
    tagline: "Capture the complete story of your educational journey.",
    intro: [
      "Your education is more than a list of degrees. It is a timeline of the schools, campuses, classrooms, teachers, classmates, courses, and experiences that helped shape who you are.",
      "AlumniPulse.net lets you build a complete education timeline — from kindergarten and primary school through high school, college, university, graduate school, professional programs, and beyond.",
    ],
    sections: [
      { heading: "Record Every Chapter", body: "Add every institution, campus, department, course, class, section, and batch you were part of. Include the years you attended, locations, qualifications, subjects, activities, achievements, and the people you remember along the way." },
      { heading: "Preserve Your Educational History", body: "Keep your educational history organized in one place instead of scattered across old documents, photographs, certificates, and memories. Your timeline can become a lasting personal record of where you learned and the communities you belonged to." },
      { heading: "Reconnect Along the Way", body: "Every chapter of your timeline can help you discover the classmates, teachers, friends, and alumni who shared that part of your journey." },
    ],
    closing: "From your first classroom to your highest degree — record every chapter, remember every connection, and preserve the journey for a lifetime.",
    cta: { label: "Start your timeline", to: "/register" },
  },
  "community-chat": {
    icon: ChatCircleDots,
    title: "A Chat Room Per Community",
    tagline: "Keep the conversation going — today, tomorrow, and years from now.",
    intro: [
      "Every class, batch, school, college, and reunion group on AlumniPulse.net gets its own persistent community chat, automatically created when the group is formed.",
      "Reconnect with classmates, share memories, exchange updates, plan reunions, celebrate achievements, and continue conversations that began years ago — all in one place.",
    ],
    sections: [
      { heading: "One Group. One Conversation.", body: "Each community has its own dedicated chat, keeping conversations organized and connected to the people who share that experience." },
      { heading: "Built to Last", body: "Unlike a temporary group conversation, your alumni community chat can remain available as your group grows. New members can join, reconnect with old conversations, and contribute to the community over time." },
      { heading: "More Than Chat", body: "Share photos, memories, announcements, reunion plans, achievements, questions, recommendations, and stories with the people who were part of your journey." },
    ],
    closing: "Your class may have graduated years ago. Your conversation doesn't have to end.",
    kicker: "Join your community. Start talking. Stay connected.",
    cta: { label: "Explore alumni communities", to: "/alumni-groups" },
  },
  "find-a-classmate": {
    icon: MagnifyingGlass,
    title: "Find a Classmate",
    tagline: "Rediscover the people who shared your journey.",
    intro: [
      "Remember someone from your school days but lost touch over the years? AlumniPulse.net makes it easier to find former classmates, friends, teachers, and colleagues through a privacy-first global alumni directory.",
      "Search using details such as name, school, college, university, campus, department, class, batch, graduation year, city, or profession to narrow your search and find the person you're looking for.",
    ],
    sections: [
      { heading: "Connect Without Giving Up Privacy", body: "When you find someone, you can use our built-in messaging system to send a private message and reconnect — without needing to publicly share personal contact information." },
      { heading: "Search Across Your Educational Journey", body: "Whether you're looking for a childhood classmate, a college roommate, a former teacher, or someone from a long-ago reunion, your education timeline can help you discover where your paths crossed." },
    ],
    closing: "Someone you knew may be just one search away.",
    kicker: "Search. Find. Message. Reconnect.",
    cta: { label: "Find a classmate", to: "/find" },
  },
  "resume-builder": {
    icon: FileText,
    title: "AI Resume Builder",
    tagline: "Turn your education and experience into a polished resume — in minutes.",
    intro: [
      "Your AlumniPulse.net profile already contains the story of your education, achievements, skills, experience, and professional journey. Our AI Resume Builder helps transform that information into a professional, well-structured resume you can refine, download, and share.",
    ],
    sections: [
      { heading: "Build From Your Profile", body: "Start with the information you've already added to AlumniPulse.net. The AI can organize your education, degrees, courses, achievements, skills, work experience, certifications, and accomplishments into a clear and professional resume." },
      { heading: "Import Your Existing Resume", body: "Already have a resume? Upload your LinkedIn PDF or existing resume and let AlumniPulse.net extract and organize the information into your profile and resume format." },
      { heading: "Customize for Your Goals", body: "Create different versions of your resume for jobs, internships, academic programs, professional opportunities, volunteering, or networking. Review, edit, and personalize the content before sharing." },
      { heading: "Export & Share", body: "Download your finished resume and share it with employers, colleagues, classmates, alumni, or professional networks." },
    ],
    closing: "Your journey. Your achievements. Your story — professionally presented.",
    kicker: "Build your resume. Refine your story. Open new opportunities.",
    cta: { label: "Build my resume", to: "/register" },
  },
  "events-reunions": {
    icon: CalendarStar,
    title: "Events & Reunions",
    tagline: "Bring your alumni community together — wherever you are.",
    intro: [
      "From a small class get-together to a large school reunion, AlumniPulse.net makes it easier to plan, organize, and celebrate the moments that bring old friends back together.",
      "Create events for your class, batch, school, college, department, university, or alumni community and invite the people who shared your journey.",
    ],
    sections: [
      { heading: "Plan With Ease", body: "Create an event with the date, time, location, description, photos, and organizer information. Keep everyone informed with event updates and announcements." },
      { heading: "RSVP & Guest Lists", body: "Know who's coming before the big day. Members can RSVP, confirm attendance, indicate guests, and update their responses, giving organizers a clear picture of participation." },
      { heading: "In-Person or Virtual", body: "Hosting a reunion across different cities or countries? Add a virtual meeting link so classmates can join from anywhere in the world." },
      { heading: "Keep the Memories", body: "After the event, continue the story. Share photos, videos, messages, highlights, and memories with the community so the reunion lives on long after everyone goes home." },
    ],
    closing: "From \u201cRemember when\u2026\u201d to \u201cGreat seeing you again!\u201d",
    kicker: "Create an event. Invite your people. Make the next reunion unforgettable.",
    cta: { label: "Browse events", to: "/events" },
  },
  "splink-qr": {
    icon: QrCode,
    title: "SpLink Short URLs + QR Codes",
    tagline: "Give every part of your AlumniPulse journey a link people can easily remember and share.",
    intro: [
      "Every AlumniPulse profile, alumni group, education timeline, resume, event, and public page can have its own branded short URL and QR code through SpLink.",
    ],
    sections: [
      { heading: "Easy to Share", body: "Turn long profile and page addresses into simple, memorable short links that are easy to send by text, email, social media, business cards, resumes, and invitations." },
      { heading: "Scan & Connect", body: "Every short link can be paired with a QR code, making it easy for someone to scan and instantly access your profile, resume, event, or other shared page from a phone." },
      { heading: "One Link, Many Possibilities", body: "Use your personalized link and QR code on business cards, resumes, name badges, reunion invitations, presentations, social profiles, or email signatures." },
      { heading: "Built for AlumniPulse", body: "Your links can help connect the different parts of your AlumniPulse identity — from your education history and alumni groups to your professional resume and events." },
    ],
    closing: "One profile. One memorable link. One simple way to connect.",
    kicker: "Create. Share. Scan. Reconnect.",
    cta: { label: "Get started", to: "/register" },
  },
};

export const FEATURE_SLUGS = Object.keys(FEATURES);

export default function FeaturePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const f = FEATURES[slug];
  useSeo(f ? `${f.title} — AlumniPulse` : "Feature", f ? f.tagline : "");
  if (!f) return <Navigate to="/" replace />;
  const Icon = f.icon;
  return (
    <main className="mx-auto max-w-3xl px-5 py-14" data-testid={`feature-page-${slug}`}>
      <div className="mb-8 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={15} /> Back to home</Link>
        <button onClick={() => (window.history.length > 2 ? navigate(-1) : navigate("/"))} aria-label="Close" data-testid="feature-close-btn" className="grid h-9 w-9 place-items-center rounded-full border border-border/60 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"><X size={18} /></button>
      </div>

      <div className="flex items-start gap-4">
        <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-accent text-primary"><Icon size={28} weight="duotone" /></span>
        <div>
          <h1 className="font-heading text-3xl font-light leading-tight tracking-tight sm:text-4xl">{f.title}</h1>
          <p className="mt-2 text-base text-secondary">{f.tagline}</p>
        </div>
      </div>

      <div className="mt-8 space-y-4">
        {f.intro.map((p, i) => <p key={i} className="text-base leading-relaxed text-foreground/85">{p}</p>)}
      </div>

      <div className="mt-10 space-y-7">
        {f.sections.map((s) => (
          <div key={s.heading} className="border-l-2 border-secondary/40 pl-5">
            <h2 className="flex items-center gap-2 font-heading text-lg font-medium"><CheckCircle size={18} weight="fill" className="text-secondary" /> {s.heading}</h2>
            <p className="mt-1.5 leading-relaxed text-muted-foreground">{s.body}</p>
          </div>
        ))}
      </div>

      <div className="mt-12 rounded-2xl border border-border/60 bg-card p-7 text-center">
        <p className="font-heading text-xl font-light leading-snug">{f.closing}</p>
        {f.kicker && <p className="mt-2 text-sm font-medium text-secondary">{f.kicker}</p>}
        <Link to={f.cta.to} className="mt-5 inline-block"><Button size="lg" className="rounded-full gap-2 px-7" data-testid="feature-cta">{f.cta.label} <ArrowRight size={18} /></Button></Link>
      </div>
    </main>
  );
}
